import React, { useCallback, useState } from "react";
import { PrivacyCenterNoticeDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { UseDataRetentionModule } from "../../../../components/organisms/data-retention/DataRetentionModule";
import { RetentionPolicyBuilder } from "../../../../components/organisms/data-retention/RetentionPolicyBuilder";
import {
  RetentionPolicyLayout,
  RetentionPolicyPreview,
  usePreviewOpen,
} from "../../../../components/organisms/data-retention/RetentionPolicyPreview";
import { ButtonGroup, ExpandablePanel, Modal, SectionEditor } from "@truevault/aquilo";
import { Form } from "react-final-form";
import { useUpdateNoticeApi } from "../../../../components/privacyCenter/EditPrivacyCenter";
import _ from "lodash";
import { CircularProgress } from "@mui/material";

// The `loading` state is because when switching between editor types we send an API request to save the selected type of editor, so show a loading state while that's happening
type RetentionPolicyEditorType = "builder" | "add-your-own" | "loading";

export function RetentionPolicyEditor({
  header,
  organizationId,
  privacyCenterId,
  refreshNotice,
  draftNotice,
  dataRetentionModule,
  showHelpModal,
  setShowHelpModal,
  doneLabel,
}: {
  // Page title block, laid out in the left column above the explainer.
  header?: React.ReactNode;
  /** The page's finish button, which the builder points to once every step is saved, if any. */
  doneLabel?: string;
  organizationId: string;
  privacyCenterId: string;
  refreshNotice: () => Promise<void>;
  draftNotice: PrivacyCenterNoticeDto;
  dataRetentionModule: UseDataRetentionModule;
  showHelpModal: boolean;
  setShowHelpModal: (show: boolean) => void;
}) {
  const [editorType, setEditorType] = useState<RetentionPolicyEditorType>(
    draftNotice.useCustomRetentionPolicySection ? "add-your-own" : "builder",
  );

  const dataRetentionSection = draftNotice.sections.find(
    (s) => s.anchor === "data-retention-policy",
  );
  const [updatedContent, setUpdatedContent] = useState<string>(dataRetentionSection.content);
  const { updateNotice } = useUpdateNoticeApi();
  const [isPreviewOpen, setIsPreviewOpen] = usePreviewOpen();

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          sections: [{ id: dataRetentionSection.id, content: newContent }],
        },
      };

      await updateNotice(updateNoticeBody);

      await refreshNotice();
    }, 750),
    [],
  );

  const handleSectionChange = useCallback(
    (newContent: string) => {
      setUpdatedContent(newContent);
      debouncedSave(newContent);
    },
    [setUpdatedContent],
  );

  const intro = (
    <>
      {header}
      <ExpandablePanel
        title="What is a Data Retention Policy?"
        titleFontSize="base"
        widthClass="w-full"
        variant="hairline"
      >
        <p className="mb-0 mt-[12px] text-[14px] leading-[20px] text-neutral-600">
          Privacy laws let your business keep consumer data only as long as it needs it for the
          purpose it was collected for. For each category of data you collect, your privacy policy
          must state either:
        </p>
        <ul className="mb-0 mt-[8px] list-disc pl-[20px] text-[14px] leading-[22px] text-neutral-600">
          <li>
            <span className="font-medium text-neutral-800">how long you keep it</span> — for
            example, 3 years, or
          </li>
          <li>
            <span className="font-medium text-neutral-800">how you decide when to delete it</span> —
            for example, when the consumer opts out.
          </li>
        </ul>
        <p className="mb-0 mt-[12px] text-[14px] leading-[20px] text-neutral-600">
          There is no legal minimum or maximum period. Choose what fits your business, or write your
          own rule. For more information, check out:{" "}
          <a
            className="text-primary-600 font-medium underline"
            href="https://knowledge.truevault.com/article/196-create-a-data-retention-policy"
          >
            How to Create a Data Retention Policy
          </a>
        </p>
        <p className="mb-0 mt-[12px] text-[14px] leading-[20px] font-medium text-primary-700">
          This policy will not automate deletion of the consumer's data — your company will have to
          enforce this.
        </p>
      </ExpandablePanel>
      <div className="mt-[24px] flex items-center gap-[16px]">
        <div className="text-[14px] font-medium text-neutral-900">You are currently using</div>
        <ButtonGroup
          fullWidth={false}
          selectedIdx={editorType === "add-your-own" ? 1 : 0}
          onClick={async (idx) => {
            let newEditorType: RetentionPolicyEditorType = idx === 0 ? "builder" : "add-your-own";
            setEditorType("loading");
            await updateNotice({
              organizationId: organizationId,
              privacyCenterId: privacyCenterId,
              noticeId: draftNotice.publicId,
              updateNoticeBody: {
                useCustomRetentionPolicySection: newEditorType === "add-your-own",
              },
            });
            // The page's Done check reads draftNotice.useCustomRetentionPolicySection.
            await refreshNotice();
            setEditorType(newEditorType);
          }}
        />
      </div>
    </>
  );

  return (
    <div>
      {/* The layouts' Tailwind utilities need an ancestor tw-boundary; utilities on the boundary
          element itself would be ignored. */}
      <div className="tw-boundary">
        {editorType === "builder" ? (
          <RetentionPolicyBuilder
            header={intro}
            doneLabel={doneLabel}
            {...dataRetentionModule.props}
          />
        ) : (
          <RetentionPolicyLayout
            isPreviewOpen={isPreviewOpen}
            onShowPreview={() => setIsPreviewOpen(true)}
            preview={
              <RetentionPolicyPreview
                introHtml=""
                rows={[]}
                customHtml={editorType === "add-your-own" ? updatedContent : ""}
                onHide={() => setIsPreviewOpen(false)}
              />
            }
            main={
              <>
                {intro}
                {editorType === "loading" && (
                  <div className="mt-[24px]">
                    <CircularProgress size="32px" />
                  </div>
                )}
                {editorType === "add-your-own" && (
                  <div className="mt-[24px]">
                    <Form
                      onSubmit={() => {}}
                      render={(_formRenderProps) => (
                        <SectionEditor
                          hideNavigationCheckbox={true}
                          hideSideControls={true}
                          addToNavigation={false}
                          header={dataRetentionSection.header}
                          content={updatedContent}
                          locked={false}
                          placeholder={dataRetentionSection.placeholder}
                          onPositionUp={() => {}}
                          onPositionDown={() => {}}
                          moveDisabled={true}
                          moveUpDisabled={true}
                          moveDownDisabled={true}
                          deleteDisabled={true}
                          onShowInNavigationCheckboxToggle={() => {}}
                          onDelete={() => {}}
                          onInputChange={({ content }) => {
                            handleSectionChange(content);
                          }}
                          onUnlock={() => {}}
                          enablePrivacyCenterToolbar={true}
                        />
                      )}
                    />
                  </div>
                )}
              </>
            }
          />
        )}
      </div>

      {showHelpModal && (
        <Modal
          title="Add your Retention Policy"
          firstButton="Got it"
          firstButtonColor="primary"
          alignButton="center"
          widthButton="full"
          maxPanelWidth="max-w-lg"
          showOverlay={true}
          closeButton={true}
          closeOnOverlayClick={true}
          onClose={() => setShowHelpModal(false)}
        >
          <p className="text-gray-500">
            Use our Retention Policy Builder or add your own. Your retention policy will show on
            your privacy policy website under your US State Notices and will let your consumers know
            how long you keep their data and for what purpose.
          </p>
          <img
            src="/assets/images/stay_compliant/privacy_center/notices-generation-complete-person-with-checkbox.svg"
            className="mx-auto mt-4"
          />
        </Modal>
      )}
    </div>
  );
}
